import React from 'react';
import PropTypes from 'prop-types';
import './ui.css';

const ARIA_SORT = { asc: 'ascending', desc: 'descending' };
const INTERACTIVE = 'button, a, input, select, textarea, [role="button"]';

/**
 * Table shell: its own scroll container (overflow-x/y) with a sticky header;
 * sortable headers are buttons and the sorted column carries aria-sort; a
 * header can carry an extra control (`info`, e.g. a tooltip trigger) beside
 * its sort button. Rows with `onRowClick` are focusable and activate on
 * click, Enter or Space (but not when the event comes from a control inside
 * the row); `onRowEnter` / `onRowSpace` give those keys their own action;
 * ArrowUp / ArrowDown move focus between rows; `onRowDoubleClick` handles a
 * double-click (not from a control). A column with `onCellClick`
 * takes clicks on its cells instead of the row. The selected row is marked
 * with aria-selected.
 */
export function DataTable({
    label, columns, rows, getRowId, sort, onSortChange, onRowClick, onRowEnter, onRowSpace, onRowDoubleClick,
    selectedId, empty, maxHeight, compact = false, className,
}) {
    const toggleSort = (col) => {
        if (!onSortChange) return;
        if (sort?.key === col.key) {
            onSortChange({ key: col.key, direction: sort.direction === 'asc' ? 'desc' : 'asc' });
        } else {
            onSortChange({ key: col.key, direction: col.defaultDirection || 'asc' });
        }
    };

    const fromControl = (e) => {
        const hit = e.target.closest?.(INTERACTIVE);
        return Boolean(hit && hit !== e.currentTarget && e.currentTarget.contains(hit));
    };

    const onRowKeyDown = (row) => (e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const action = e.key === 'Enter' ? (onRowEnter || onRowClick) : (onRowSpace || onRowClick);
            action(row);
            return;
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const next = e.key === 'ArrowDown' ? e.currentTarget.nextElementSibling : e.currentTarget.previousElementSibling;
            next?.focus();
        }
    };

    return (
        <div
            className={['v2-tscroll', compact && 'v2-compact', className].filter(Boolean).join(' ')}
            style={maxHeight ? { maxHeight } : undefined}
        >
            <table className="v2-table" aria-label={label}>
                <thead>
                    <tr>
                        {columns.map((col) => {
                            const sorted = sort?.key === col.key;
                            const right = col.align === 'right';
                            let arrow = '↕';
                            if (sorted) arrow = sort.direction === 'asc' ? '▲' : '▼';
                            const head = col.sortable ? (
                                <button type="button" onClick={() => toggleSort(col)}>
                                    {col.label}
                                    <span className="v2-ar" aria-hidden="true">{arrow}</span>
                                </button>
                            ) : (
                                <span>{col.label}</span>
                            );
                            return (
                                <th
                                    key={col.key}
                                    scope="col"
                                    className={[right && 'v2-r', col.className].filter(Boolean).join(' ') || undefined}
                                    aria-sort={sorted ? ARIA_SORT[sort.direction] : undefined}
                                    style={col.width ? { width: col.width } : undefined}
                                >
                                    {col.info ? <div className="v2-th-split">{head}{col.info}</div> : head}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const id = getRowId(row);
                        return (
                            <tr
                                key={id}
                                data-row-id={id}
                                tabIndex={onRowClick ? 0 : undefined}
                                aria-selected={selectedId !== undefined ? id === selectedId : undefined}
                                className={onRowClick ? 'v2-row-action' : undefined}
                                onClick={onRowClick ? (e) => { if (!fromControl(e)) onRowClick(row); } : undefined}
                                onKeyDown={onRowClick ? onRowKeyDown(row) : undefined}
                                onDoubleClick={onRowDoubleClick ? (e) => { if (!fromControl(e)) onRowDoubleClick(row); } : undefined}
                            >
                                {columns.map((col) => (
                                    <td
                                        key={col.key}
                                        className={[col.align === 'right' && 'v2-r', col.className, col.onCellClick && 'v2-cell-action']
                                            .filter(Boolean).join(' ') || undefined}
                                        onClick={col.onCellClick ? (e) => {
                                            if (fromControl(e)) return;
                                            e.stopPropagation();
                                            col.onCellClick(row, e);
                                        } : undefined}
                                    >
                                        {col.render ? col.render(row) : row[col.key]}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {rows.length === 0 && empty && <div className="v2-table-empty">{empty}</div>}
        </div>
    );
}

DataTable.propTypes = {
    label: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
        sortable: PropTypes.bool,
        align: PropTypes.oneOf(['left', 'right']),
        defaultDirection: PropTypes.oneOf(['asc', 'desc']),
        render: PropTypes.func,
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        info: PropTypes.node,
        onCellClick: PropTypes.func,
        className: PropTypes.string,
    })).isRequired,
    rows: PropTypes.array.isRequired,
    getRowId: PropTypes.func.isRequired,
    sort: PropTypes.shape({ key: PropTypes.string, direction: PropTypes.oneOf(['asc', 'desc']) }),
    onSortChange: PropTypes.func,
    onRowClick: PropTypes.func,
    onRowEnter: PropTypes.func,
    onRowSpace: PropTypes.func,
    onRowDoubleClick: PropTypes.func,
    className: PropTypes.string,
    selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    empty: PropTypes.node,
    maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    compact: PropTypes.bool,
};
